import { useEffect, useRef, useState } from 'react'

import { F0Box, F0Button } from '@factorialco/f0-react'
import { RichTextDisplay } from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'

const COLLAPSE_THRESHOLD_LINES = 10
const COLLAPSED_VISIBLE_LINES = 8
const FADE_LINES = 3

type CollapsibleRichTextProps = {
  content: string
}

const CollapsibleRichText = ({ content }: CollapsibleRichTextProps) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [showToggle, setShowToggle] = useState(false)
  const [collapsedHeight, setCollapsedHeight] = useState<number | null>(null)
  const [fadeHeight, setFadeHeight] = useState<number | null>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const contentId = useRef(`collapsible-rich-text-${Math.random().toString(36).slice(2)}`)

  useEffect(() => {
    setIsExpanded(false)
  }, [content])

  useEffect(() => {
    const element = contentRef.current

    if (!element) return

    const updateMeasurements = () => {
      const richTextElement = element.querySelector<HTMLElement>('[class*="rich-text"]')

      if (!richTextElement) {
        setCollapsedHeight(null)
        setFadeHeight(null)
        setShowToggle(false)
        return
      }

      const richTextLineHeight = Number.parseFloat(
        window.getComputedStyle(richTextElement).lineHeight
      )
      const firstBlockLineHeight = Number.parseFloat(
        window.getComputedStyle(richTextElement.firstElementChild ?? richTextElement).lineHeight
      )
      const effectiveLineHeight =
        (Number.isFinite(richTextLineHeight) && richTextLineHeight) ||
        (Number.isFinite(firstBlockLineHeight) && firstBlockLineHeight) ||
        24

      setCollapsedHeight(effectiveLineHeight * COLLAPSED_VISIBLE_LINES)
      setFadeHeight(effectiveLineHeight * FADE_LINES)
      setShowToggle(richTextElement.scrollHeight > effectiveLineHeight * COLLAPSE_THRESHOLD_LINES)
    }

    const frame = window.requestAnimationFrame(updateMeasurements)
    const resizeObserver = new ResizeObserver(updateMeasurements)
    resizeObserver.observe(element)

    return () => {
      window.cancelAnimationFrame(frame)
      resizeObserver.disconnect()
    }
  }, [content])

  return (
    <F0Box display="flex" flexDirection="column" gap="sm">
      {/* eslint-disable-next-line factorial/no-raw-html -- RichTextDisplay needs a DOM wrapper for height measurement and clipping */}
      <div
        className="w-full max-w-screen-sm"
        id={contentId.current}
        ref={contentRef}
        style={{
          position: 'relative',
          overflow: !isExpanded && showToggle ? 'hidden' : undefined,
          maxHeight: !isExpanded && showToggle ? (collapsedHeight ?? undefined) : undefined,
        }}
      >
        <RichTextDisplay content={content} />
        {showToggle && !isExpanded && fadeHeight && (
          /* eslint-disable-next-line factorial/no-raw-html -- overlay div is required for the fade effect above rich text */
          <div
            aria-hidden
            style={{
              position: 'absolute',
              right: 0,
              bottom: 0,
              left: 0,
              height: fadeHeight,
              pointerEvents: 'none',
              background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, #fff 100%)',
            }}
          />
        )}
      </div>
      {showToggle && (
        <F0Box display="flex" alignItems="start">
          <F0Button
            variant="neutral"
            size="sm"
            label={String(isExpanded ? i18n.t('common.see_less') : i18n.t('common.read_more'))}
            aria-expanded={isExpanded}
            aria-controls={contentId.current}
            onClick={() => setIsExpanded((expanded) => !expanded)}
          />
        </F0Box>
      )}
    </F0Box>
  )
}

export default CollapsibleRichText
